<script setup lang="ts">
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import { useTemplateRef } from "vue";
import { useModalStore } from "@/stores/modal";

const emits = defineEmits<{
  (e: "success"): void;
  (e: "close"): void;
}>();
const router = useRouter();
const { isLoggedIn, login } = useUser();
const { t } = useI18n();
const { refreshSessionContext } = useSessionContext();
const { mergeWishlistProducts } = useWishlist();
const { pushSuccess } = useNotifications();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const loginErrors = ref<string[]>([]);
const loading = ref<boolean>(false);

const formData = ref({
  username: "",
  password: "",
  remember: true,
});

const goToRegister = () => {
  emits("close");
  router.push(formatLink("/register"));
};

const { resolveApiErrors } = useApiErrorsResolver("account_login");

const invokeLogin = async (): Promise<void> => {
  loginErrors.value = [];
  try {
    await refreshSessionContext();
    loading.value = true;
    await login(formData.value);
    emits("success");
    pushSuccess(t("account.messages.loggedInSuccess"));
    emits("close");
    mergeWishlistProducts();
    loading.value = false;
  } catch (error) {
    loading.value = false;
    // if (error instanceof ApiClientError) {
      loginErrors.value = resolveApiErrors(error.details.errors as ApiError[]);
    // }
  }
};

const emailInputElement = useTemplateRef("emailInputElement");
useFocus(emailInputElement, { initialValue: true });
const { logoData } = useLogo();
</script>
<template>
  <div
    id="modal-headline"
    role="form"
    title="Login form"
    :aria-label="$t('loginForm.loginFormAriaLabel')"
    class="min-h-full flex flex-col p-0 md:p-6"
  >
    <div v-if="!isLoggedIn" class="max-w-full xl:max-w-md w-full space-y-8 mx-auto">
      <div>
        <img
          class="mx-auto h-12 mb-6 w-[180px] md:w-[200px] lg:w-[260px] xl:w-[322px]"
          :src="logoData?.url"
          :alt="logoData?.alt ?? 'logo of the shop'"
          width="247"
          height="48"
        />
        <hr/>
        <h2 class="mt-6 text-center text-md md:text-lg font-normal text-primary mb-6">
          {{ $t("account.signInLabel") }}
        </h2>
      </div>
      <form class="mt-6 space-y-6" @submit.prevent="invokeLogin">
        <input
          v-model="formData.remember"
          type="hidden"
          name="remember"
          data-testid="login-remember-input"
        />
        <div class="rounded-md shadow-sm space-y-[10px]">
          <div>
            <label for="email-address" class="sr-only">{{
              $t("form.email")
            }}</label>
            <input
              id="email-address"
              ref="emailInputElement"
              v-model="formData.username"
              name="email"
              type="email"
              autocomplete="email"
              required
              class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
              :placeholder="$t('form.email')"
              data-testid="login-email-input"
            />
          </div>
          <div class="mt-4">
            <label for="password" class="sr-only">{{
              $t("form.password")
            }}</label>
            <input
              id="password"
              v-model="formData.password"
              name="password"
              type="password"
              autocomplete="current-password"
              required
              class="appearance-none relative block w-full px-3 py-3 border placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-base border-secondary-300 focus:border-primary"
              :placeholder="$t('form.password')"
              data-testid="login-password-input"
            />
          </div>
        </div>

        <slot name="error">
          <div
            v-if="loginErrors.length"
            class="flex items-center justify-between"
            data-testid="login-errors-container"
          >
            <div class="flex items-center">
              <div
                class="login-errors text-red focus:ring-primary border-secondary-300 rounded"
              >
                <div v-for="error in loginErrors" class="login-errors text-red focus:ring-primary border-secondary-300 rounded">
                  {{ error }}
                </div>
              </div>
            </div>
          </div>
        </slot>

        <div class="flex flex-row gap-4">
          <button
            class="btn btn-primary w-full justify-center"
            type="submit"
            data-testid="login-submit-button"
            :disabled="loading"
          >
            <span class="my-auto">
              {{ $t("account.signIn") }}
            </span>
          </button>

          <slot name="action">
            <div class="w-full" @click="$emit('close')">
              <button
                type="button"
                class="btn btn-secondary w-full justify-center"
                data-testid="login-sign-up-link"
                @click="goToRegister()"
              >
                {{ $t("account.signUp") }}
              </button>
            </div>
          </slot>
        </div>

        <slot :data="formData" />
      </form>
    </div>
    <div v-else>
      <h2>{{ $t("account.loggedInInfo") }}</h2>
      <button
        class="btn btn-primary w-full justify-center mt-6"
        @click="$emit('close')"
      >
        close
      </button>
    </div>
  </div>
</template>
